import { readAccessRelations, readAccessTargets } from '@/api/core/access-workflow';
import { canReadDomain, coreQueryKey } from '@/api/core/authorization';
import { useOperatorSession } from '@/auth/runtime';
import { CopyIdButton } from '@/components/copy-id-button';
import { LoadFailure } from '@/components/load-failure';
import { Button } from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { propertyRead, type PropertyRuntime } from '../property/property-queries';

interface SlotLabel {
  room: string | null;
  name: string | null;
}
const issueLabels: Record<string, string> = {
  REQUESTED: '요청됨',
  DISPATCHING: '전송 중',
  ACCEPTED: '요청 수락',
  APPLIED: '적용 기록',
  VERIFIED: '검증 기록',
  FAILED: '실패 기록',
  UNKNOWN: '결과 미확정',
  SUPERSEDED: '후속 요청으로 대체',
};

/**
 * 장비 하나의 저장된 Unit 연결과, 그 Unit의 투숙에 저장된 발급 근거를 읽는다.
 * 어느 쪽도 지금 출입이 가능한지, 실제 장비에 적용됐는지를 알려 주지 않는다.
 */
export function DeviceUsagePanel({
  runtime,
  propertyId,
  deviceId,
  slots,
}: {
  runtime: PropertyRuntime;
  propertyId: string;
  /** Core 물리 출입 그룹 UUID(core_device_id). */
  deviceId: string;
  slots: Record<string, SlotLabel>;
}) {
  const session = useOperatorSession(runtime.session);
  const [open, setOpen] = useState(false);
  const [targetCursor, setTargetCursor] = useState<string | null>(null);
  const [relationCursor, setRelationCursor] = useState<string | null>(null);
  const authorization = session.authorization;
  const canRelations =
    session.status === 'authorized' &&
    !session.deniedDomains.includes('property') &&
    !!authorization &&
    canReadDomain(authorization, 'property', 1);
  const canTargets =
    canRelations &&
    !session.deniedDomains.includes('entitlement') &&
    canReadDomain(authorization, 'entitlement', 1);
  const current = () => runtime.session.getSnapshot().generation === session.generation;
  const relations = useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, session.generation, 'access-relations', propertyId),
      deviceId,
      relationCursor,
    ],
    queryFn: ({ signal }) =>
      propertyRead(
        runtime,
        session.generation,
        () =>
          readAccessRelations(
            runtime.client,
            propertyId,
            { deviceId, cursor: relationCursor },
            signal,
          ),
        signal,
        current,
      ),
    enabled: open && canRelations,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    meta: { suppressGlobalError: true },
  });
  const targets = useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, session.generation, 'access-targets', propertyId),
      deviceId,
      targetCursor,
    ],
    // 403은 지점·출입 자격 중 어느 권한 때문인지 알 수 없어 영역 차단으로 올리지 않고 이 패널에만 보인다.
    queryFn: ({ signal }) =>
      readAccessTargets(runtime.client, propertyId, deviceId, targetCursor, signal),
    enabled: open && canTargets,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    meta: { suppressGlobalError: true },
  });
  if (!open)
    return (
      <Button
        className="mt-3"
        variant="outline"
        size="sm"
        onClick={() => {
          setOpen(true);
        }}
      >
        연결·사용 현황 보기
      </Button>
    );
  return (
    <section aria-label="장비 연결·사용 현황" className="mt-3 space-y-3 border-t pt-3">
      <p className="caption text-muted-foreground">
        저장된 연결과 발급 기록입니다. 실제 장비 적용 여부와 지금 출입 가능 여부는 확인하지
        않습니다.
      </p>
      <div className="space-y-1">
        <h3 className="font-medium">이 장비를 쓰는 슬롯</h3>
        {!canRelations && <p>지점 조회 권한이 필요합니다.</p>}
        {relations.isPending && canRelations && <p role="status">연결을 조회하고 있습니다.</p>}
        {relations.isError && (
          <LoadFailure
            error={relations.error}
            subject="장비 연결"
            onRetry={() => void relations.refetch()}
          />
        )}
        {canRelations && relations.isSuccess && !relations.data.items.length && (
          <p>저장된 슬롯 연결이 없습니다.</p>
        )}
        {canRelations && relations.isSuccess && (
          <ul className="space-y-1">
            {relations.data.items.map((relation) => {
              const slot = slots[relation.unit_id];
              return (
                <li key={relation.relation_id} className="min-w-0">
                  {slot ? (
                    `${slot.room ?? '객실명 없음'} · ${slot.name ?? '슬롯명 없음'}`
                  ) : (
                    <div className="space-y-1">
                      <p>객실·슬롯 이름 미확인</p>
                      <div className="flex min-w-0 items-start gap-1">
                        <span>슬롯 ID</span>
                        <span className="min-w-0 flex-1 font-mono break-all whitespace-normal">
                          {relation.unit_id}
                        </span>
                        <CopyIdButton value={relation.unit_id} label="슬롯 ID" />
                      </div>
                      <div className="flex min-w-0 items-start gap-1">
                        <span>객실 ID</span>
                        <span className="min-w-0 flex-1 font-mono break-all whitespace-normal">
                          {relation.room_id}
                        </span>
                        <CopyIdButton value={relation.room_id} label="객실 ID" />
                      </div>
                    </div>
                  )}{' '}
                  · 활성 설정{' '}
                  {relation.configured_active === null
                    ? '확인 안 됨'
                    : relation.configured_active
                      ? '활성'
                      : '비활성'}
                </li>
              );
            })}
          </ul>
        )}
        {canRelations && relations.isSuccess && (
          <div className="flex flex-wrap gap-2">
            {relationCursor && (
              <Button
                variant="outline"
                size="sm"
                disabled={relations.isFetching}
                onClick={() => {
                  setRelationCursor(null);
                }}
              >
                첫 연결부터
              </Button>
            )}
            {relations.data.next_cursor && (
              <Button
                variant="outline"
                size="sm"
                disabled={relations.isFetching}
                onClick={() => {
                  setRelationCursor(relations.data.next_cursor);
                }}
              >
                다음 연결 보기
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="space-y-1">
        <h3 className="font-medium">저장된 투숙 발급 기록</h3>
        {!canTargets && canRelations && (
          <p>출입 자격 조회 권한이 있어야 투숙 발급 기록을 볼 수 있습니다.</p>
        )}
        {targets.isPending && canTargets && <p role="status">발급 기록을 조회하고 있습니다.</p>}
        {targets.isError && (
          <LoadFailure
            error={targets.error}
            subject="투숙 발급 기록"
            onRetry={() => void targets.refetch()}
          />
        )}
        {canTargets && targets.isSuccess && !targets.data.items.length && (
          <p>이 시점에 저장된 발급 기록이 없습니다. 출입 대상이 없다는 뜻은 아닙니다.</p>
        )}
        {canTargets && targets.isSuccess && (
          <ul className="space-y-2">
            {targets.data.items.map((stay) => (
              <li key={stay.stay_id}>
                <Link className="underline" to={`/stays/${stay.stay_id}`}>
                  투숙 {stay.stay_id.slice(0, 8)}
                </Link>{' '}
                · 저장 발급 {stay.period_issues.length}건
                {stay.period_issues.length === 0 && ' (기간 안 기록 없음)'}
                <ul className="caption text-muted-foreground">
                  {stay.period_issues.map(({ issue, grants }) => (
                    <li key={issue.entitlement_id}>
                      발급 상태 {issueLabels[issue.status] ?? '상태 확인 필요'} · 저장된 적용 기록{' '}
                      {grants.length}건
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
        {canTargets && targets.isSuccess && (
          <div className="flex gap-2">
            {targetCursor && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setTargetCursor(null);
                }}
              >
                처음 투숙부터
              </Button>
            )}
            {targets.data.next_cursor && (
              <Button
                size="sm"
                variant="outline"
                disabled={targets.isFetching}
                onClick={() => {
                  setTargetCursor(targets.data.next_cursor);
                }}
              >
                다음 투숙 보기
              </Button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
